import moment from "moment";
import { DateTime } from "luxon";
import { createContext, useContext, useState } from "react";
import { isEdge, isNode } from "react-flow-renderer";
//Modifications for dispatch
import { useDispatch } from "react-redux";
import { handleUpdateFlowStatus } from "../state/activedFlows/actions";

const GanttContext = createContext();
const nowLocal = DateTime.now();

export function GanttProvider({ children }) {
  const addDays = (currentDate, number_of_days, node) => {
    if (node.type === "task" && node.data.status === "doing") {
      const deadline = node.data.expiration.date;

      if (deadline < nowLocal.toMillis()) {
        return new Date();
      }
    }
    //
    // let updateDate = new Date(currentDate.getTime());
    // updateDate.setDate(updateDate.getDate() + number_of_days);

    let updateDate = new Date(currentDate.getTime());
    let millisecondsPerDay = 24 * 60 * 60 * 1000;
    let daysInMilliseconds = number_of_days * millisecondsPerDay;

    updateDate.setTime(updateDate.getTime() + daysInMilliseconds);

    return updateDate;
  };

  const getStart = (node, momentTime) => {
    const status = node.data.status;

    if (status === "done" || status === "doing") {
      if (node.type === "parallel") {
        return new Date(node.data.finishedAt);
      }

      return new Date(node.data.startedAt);
    } else {
      //Pending
      return new Date(momentTime);
    }
  };
  const getEnd = (node, momentTime) => {
    const status = node.data.status;

    if (status === "done") {
      //Qualquer Nó finalizado
      return new Date(node.data.finishedAt);
    } else if (
      status === "doing" &&
      (node.type === "task" || node.type === "timerEvent")
    ) {
      //Tarefas e temporizadores do momento
      //Data -> StartdAt  + Duração (Deadline)
      const fromStartToEnd = new Date(node.data.startedAt);
      const duration = node.data.expiration.number / 24;

      const newDate = addDays(fromStartToEnd, duration, node);
      return newDate;
    } else {
      //Pendentes ou condicionais em doing usam o momento do fluxo
      const fromStartToEnd = new Date(momentTime);
      if (node.type === "task" || node.type === "timerEvent") {
        const duration = node.data.expiration.number / 24;
        const newDate = addDays(fromStartToEnd, duration, node);

        return newDate;
      }
      return fromStartToEnd;
    }
  };

  const getProgress = (node) => {
    const status = node.data.status;

    if (status === "done" || status === "doing") {
      const baseTime = moment(node.data.startedAt)
        .add(node.data.expiration.number, "hours")
        .format();

      if (status === "done") {
        const finishedAt = moment(node.data.finishedAt);
        const diff = finishedAt.diff(baseTime, "hours");

        if (diff < 0) {
          return { message: "Concluído", status: "done" };
        } else {
          return { message: "Concluído com atraso", status: "doneLate" };
        }
      } else {
        const nowLocal = moment().utcOffset(-180);
        const diff = nowLocal.diff(baseTime, "hours");
        if (diff < 0) {
          return { message: "Em execução", status: "doing" };
        } else {
          return {
            message: "Atrasado em ~" + Math.round(diff / 24) + " dias",
            status: "late",
          };
        }
      }
    } else {
      return { message: "Pendente", status: "pending" };
    }
  };

  const clearGantt = async () => {
    setGanttData({
      jobs: [],
      criticalPath: [],
      criticalDuration: 0,
      status: "clear", //clear | pending | done
      flowId: null,
    });
  };

  const [ganttData, setGanttData] = useState({
    jobs: [],
    criticalPath: [],
    criticalDuration: 0,
    status: "clear", //clear | pending | done
    flowId: null,
  });

  const dispatch = useDispatch();

  const updateToJob = (element, startedAt) => {
    const elementStatus =
      element.type === "task"
        ? getProgress(element).status
        : element.data.status;

    //? Adicionar Verificação por Status e lembrar que o late pega o tempo atual.

    const job = {
      id: element.id,
      flowId: element.flowId,
      start: getStart(element, startedAt),
      end: getEnd(element, startedAt),
      label: element.data.label
        ? element.data.label
        : element.type === "timerEvent"
          ? "Temporizador"
          : "",
      type: element.type,
      status: elementStatus,
      connections: element.connections,
      accountable: element.data.accountable,
      duration: element.data.expiration,
      position: element.data.position,
    };
    return job;
  };

  function preprocessElements(elements) {
    let MappedElements = {};

    // Initial mapping, assuming this step works as intended
    elements.forEach((element) => {
      if (isNode(element) && element.data.status !== "outscoped") {
        MappedElements[element.id] = {
          ...element,
          connections: {
            next: [],
            previous: [],
          },
        };
      }
    });

    // Establish connections
    elements.forEach((element) => {
      if (isEdge(element) && element.data.status !== "outscoped") {
        const { source, target } = element;
        if (MappedElements[source]) {
          MappedElements[source].connections.next.push(target);
        }
        if (MappedElements[target]) {
          MappedElements[target].connections.previous.push(source);
        }
      }
    });

    // Correct connections for removals
    Object.values(MappedElements).forEach((element) => {
      if (element.type === "parallelEnd" || element.type === "conditionalEnd") {
        element.connections.previous.forEach((prevId) => {
          MappedElements[prevId].connections.next = MappedElements[
            prevId
          ].connections.next
            .filter((nextId) => nextId !== element.id) // Remove the end node
            .concat(element.connections.next); // Add end node's next nodes
        });
        element.connections.next.forEach((nextId) => {
          MappedElements[nextId].connections.previous = MappedElements[
            nextId
          ].connections.previous
            .filter((prevId) => prevId !== element.id) // Remove the end node
            .concat(element.connections.previous); // Add end node's previous nodes
        });

        // Finally, remove the end node itself
        delete MappedElements[element.id];
      }
    });

    return MappedElements;
  }

  const processStack = (stack, MappedElements) => {
    let doneHours = 0;
    let projectHours = 0;
    let activeJobs = [];
    let jobsList = [];

    while (stack.length > 0) {
      let currentJob = stack.pop();

      // Check if all previous jobs are processed for jobs with multiple previous connections
      if (currentJob.connections.previous.length > 1) {
        const allPreviousProcessed = currentJob.connections.previous.every(
          (prevId) => jobsList.some((job) => job.id === prevId),
        );

        // If not all previous jobs are processed, skip this iteration
        if (!allPreviousProcessed) continue;

        // Update start date to the latest end date among predecessors
        let latestEndDate = Math.max(
          ...currentJob.connections.previous.map((prevId) => {
            const prevJob = jobsList.find((job) => job.id === prevId);
            return prevJob ? new Date(prevJob.end).getTime() : 0;
          }),
        );
        currentJob.start = getStart(
          MappedElements[currentJob.id],
          latestEndDate,
        );
        currentJob.end = getEnd(MappedElements[currentJob.id], latestEndDate);
      }

      // Process single next connection
      if (currentJob.connections.next.length === 1) {
        let nextId = currentJob.connections.next[0];
        if (MappedElements[nextId]) {
          stack.push(updateToJob(MappedElements[nextId], currentJob.end));
        }
      }

      // Process multiple next connections
      else if (currentJob.connections.next.length > 1) {
        let nextJobs = currentJob.connections.next
          .map((id) => MappedElements[id])
          .sort((a, b) => b.position.y - a.position.y) // Sort by descending Y position
          .map((job) => updateToJob(job, currentJob.end));

        stack.push(...nextJobs); // Push all sorted jobs onto the stack
      }

      jobsList.push(currentJob);

      // Progression calculations
      if (currentJob.duration !== undefined) {
        projectHours += currentJob.duration.number;
        if (currentJob.status === "done") {
          doneHours += currentJob.duration.number;
        }
      }
      // Active jobs update
      if (
        (currentJob.type === "task" || currentJob.type === "timerEvent") &&
        currentJob.status === "doing"
      ) {
        activeJobs.push(currentJob);
      }
    }
    let progress = doneHours / projectHours;
    return { jobsList, progress, activeJobs };
  };

  const loadGantt = async (
    elements = [],
    flowId = "",
    flowStatus = {},
    createdAt,
  ) => {
    // Verify that all necessary parameters are provided and valid
    if (
      !elements.length ||
      !flowId ||
      !Object.keys(flowStatus).length ||
      !createdAt
    ) {
      return; // Early return if any parameter is invalid
    }

    try {
      ganttProcess(elements, flowId, flowStatus, createdAt);
    } catch (err) {
      console.error(err);
    }
  };

  const unMountedLoadGantt = async (
    elements = [],
    flowId = "",
    flowStatus = {},
    createdAt,
  ) => {
    if (
      !elements.length ||
      !flowId ||
      !Object.keys(flowStatus).length ||
      !createdAt
    ) {
      return; // Early return if any parameter is invalid
    }

    clearGantt()
      .then(() => {
        ganttProcess(elements, flowId, flowStatus, createdAt);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const ganttProcess = async (elements, flowId, flowStatus, createdAt) => {
    const MappedElements = preprocessElements(elements);

    const firstJobId = Object.keys(MappedElements).find(
      (id) => MappedElements[id].type === "eventStart",
    );

    if (!firstJobId) {
      return;
    }

    let stack = [updateToJob(MappedElements[firstJobId], createdAt)];

    const { jobsList, progress, activeJobs } = processStack(
      stack,
      MappedElements,
    );

    const finalJobList = jobsList.slice(1, jobsList.length - 1);

    const { criticalPath, criticalDuration } = buildCriticalPath(jobsList);

    setGanttData({
      jobs: finalJobList, // Array of job objects
      criticalPath: criticalPath, // Array of tasks forming the critical path
      criticalDuration: criticalDuration, // Total duration of the critical path
      status: "done", // Update status as needed
      flowId: flowId, // To Garante that that the function will only be called once and in the correct FLow
    });

    //? FlowStatus Manipulation Block

    // Parse dates from the flowStatus
    const criticalDate = flowStatus.criticalDate
      ? new Date(flowStatus.criticalDate)
      : null;
    const estimatedAt = flowStatus.estimatedAt
      ? new Date(flowStatus.estimatedAt)
      : null;

    const storedProgress = flowStatus.progress ? flowStatus.progress : null;

    const estimatedStart = new Date(createdAt);
    const estimatedEnd = new Date(finalJobList[finalJobList.length - 1].end);
    const criticalEndDate = new Date(
      estimatedStart.getTime() + criticalDuration * 60 * 60 * 1000,
    );

    // Adding the buffer time of one hour (3600000 milliseconds)
    const bufferTime = 3600000;

    const estimatedEndTime = estimatedEnd.getTime();
    let estimatedAtTime = estimatedAt ? estimatedAt.getTime() : 0;

    //! Testing Blog for Debuging
    /*
      console.log("flowStatus :", flowStatus);
      console.log("criticalDate :", criticalDate);
      console.log("estimatedEndTime :", estimatedEndTime);
      console.log("estimatedAtTime :", estimatedAtTime);
      console.log("storedProgress :", flowStatus.progress);
      console.log("progress :", progress);
      if (!criticalDate) {
        console.log("Triger no criticalDate");
      }
      if (Math.abs(estimatedEndTime - estimatedAtTime) > bufferTime) {
        console.log("Triger 1h diference");
      }
      if (Math.abs(progress - storedProgress) > 0.001) {
        console.log("Update Progress");
      }
      */
    if (
      // Acabou de ser Criado e não possui um criticalDate
      !criticalDate ||
      // Atualização no tempo de estimativa em mais de uma Hora
      Math.abs(estimatedEndTime - estimatedAtTime) > bufferTime ||
      // Qualquer atualização nas tarefas para done atualiza o Progresso
      // Devido a natureza do systema o calculo evita erros devido a possobilidade de tipos diferentes
      Math.abs(progress - storedProgress) > 0.001
    ) {
      dispatch(
        handleUpdateFlowStatus({
          flowId: flowId,
          estimatedAt: estimatedEnd.toISOString(),
          criticalDate: criticalDate
            ? criticalDate.toISOString()
            : criticalEndDate.toISOString(),
          progress: progress,
          activeJobs: activeJobs,
        }),
      );
    }
  };

  const buildCriticalPath = (jobs) => {
    let simplifiedList = [];
    let totalDuration = 0;
    let currentTask = jobs[jobs.length - 1];

    while (currentTask) {
      if (currentTask.duration && currentTask.duration.number) {
        simplifiedList.push(currentTask);
        totalDuration += currentTask.duration.number;
      }
      let previousTask = null;
      if (currentTask.connections.previous.length === 1) {
        previousTask = jobs.find(
          // eslint-disable-next-line no-loop-func
          (job) => job.id === currentTask.connections.previous[0],
        );
      } else if (currentTask.connections.previous.length > 1) {
        previousTask = currentTask.connections.previous
          .map((prevId) => jobs.find((job) => job.id === prevId))
          .reduce(
            (maxJob, currentJob) => {
              return new Date(maxJob.end) > new Date(currentJob.end)
                ? maxJob
                : currentJob;
            },
            { end: new Date(0) },
          );
      }
      currentTask = previousTask ? previousTask : null;
    }

    simplifiedList = simplifiedList.reverse();

    return {
      criticalPath: simplifiedList,
      criticalDuration: totalDuration,
    };
  };

  return (
    <GanttContext.Provider
      value={{
        jobs: ganttData.jobs,
        criticalPath: ganttData.criticalPath,
        criticalDuration: ganttData.criticalDuration,
        status: ganttData.status,
        ganttFlowId: ganttData.flowId,
        loadGantt,
        unMountedLoadGantt,
        clearGantt,
      }}
    >
      {children}
    </GanttContext.Provider>
  );
}

export function useGantt() {
  return useContext(GanttContext);
}
